/* eslint-disable @typescript-eslint/no-empty-function */
import cookieUniversal from 'cookie-universal';
import { v4 as uuidv4 } from 'uuid';
import UAParser from 'ua-parser-js';
import stringify from 'fast-safe-stringify';
import { TIDAL_TRACKING_COOKIE_NAME, TidalTrackingInterface, TIDAL_TOKEN, getTidalTrackingCookieOptions, captureUtms, } from '../tidalTracking';
import { Logger, LogLevel } from '../utils/logger';
import { ClientPlatform } from '../types/cdf';
const REQUIRED_COOKIE = 'performance';
export class TrackingApi {
}
class BaseTrackingApi extends TrackingApi {
    constructor(baseOptions) {
        super();
        this.baseOptions = baseOptions;
        this.tidalTracking = null;
        this.query = new URLSearchParams();
        this.logger = new Logger({
            prefix: baseOptions.source,
            logLevel: baseOptions.moduleOptions.logLevel || LogLevel.ERROR,
        });
        if (baseOptions.moduleOptions.integrations?.tidalTracking) {
            this.tidalTracking = new TidalTrackingInterface({
                useProductionEndpoint: baseOptions.moduleOptions.integrations?.tidalTracking.useProductionEndpoint,
                oauthClientId: baseOptions.moduleOptions.integrations?.tidalTracking.oauthClientId,
                serviceVersion: baseOptions.moduleOptions.integrations?.tidalTracking.serviceVersion,
                baseUrl: baseOptions.moduleOptions.baseUrl,
                useProxy: baseOptions.moduleOptions.integrations.tidalTracking.useProxy || false,
            });
        }
    }
    setTidalTrackingData(trackingData) {
        const cookieOptions = getTidalTrackingCookieOptions(this.baseOptions.moduleOptions.cookieOptions);
        this.baseOptions.$cookies.set(TIDAL_TRACKING_COOKIE_NAME, stringify(trackingData), cookieOptions);
    }
    getTidalTrackingData() {
        return this.baseOptions.$cookies.get(TIDAL_TRACKING_COOKIE_NAME) || {};
    }
    /**
     * Sets `trackingData.uuid`. If consent has not been granted, trackingUuid is stored in the
     * class to be recalled later
     */
    setTidalTrackingUUID(trackingUuid) {
        if (!this.isCookieAllowed(REQUIRED_COOKIE)) {
            this.overriddenTrackingUuid = trackingUuid;
            return;
        }
        const trackingData = this.getTidalTrackingData();
        this.setTidalTrackingData({
            ...trackingData,
            uuid: trackingUuid,
        });
        this.overriddenTrackingUuid = undefined;
    }
    setUtms() {
        if (!this.isCookieAllowed(REQUIRED_COOKIE)) {
            return;
        }
        const trackingData = this.getTidalTrackingData();
        if (captureUtms(trackingData, this.query)) {
            this.setTidalTrackingData(trackingData);
        }
    }
    /**
     * Enriches event with additional properties before sending. This is necessary because at the time of triggering
     * the event we might not have access to some properties due to missing consent.
     */
    async enrichAndSendCDFTidalEvent(eventData) {
        const { uuid = '' } = this.getTidalTrackingData();
        if (!uuid) {
            this.logger.error(`Missing UUID. Skipping CDF event ${eventData.event.name}`);
            return;
        }
        const clientData = this.getClientData(eventData.extraData);
        const assembledCDFEvent = this.assembleCDFEvent(eventData, clientData, uuid);
        this.logger.debug(`Sending CDF event ${eventData.event.name}`, JSON.stringify(assembledCDFEvent, null, 2));
        await this.tidalTracking?.sendCDFEvent(assembledCDFEvent);
    }
    getClientData(extraData) {
        return {
            ...this.getUAParserData(extraData.userAgent),
            currentRoute: extraData.currentRoute || null,
            experimentationIdentifier: extraData.experimentationIdentifier,
            platform: extraData.clientPlatform || ClientPlatform.CommercialWeb,
            previousRoute: extraData.previousRoute || null,
            token: this.tidalTracking?.oauthClientId || TIDAL_TOKEN,
            version: this.tidalTracking?.serviceVersion || '',
        };
    }
    getUAParserData(userAgent) {
        const ua = UAParser(userAgent);
        return {
            browser: ua.browser.name || '',
            os: ua.os.name || '',
            deviceModel: ua.device.model || '',
            deviceVendor: ua.device.vendor || '',
            deviceType: ua.device.type || '',
        };
    }
    assembleCDFEvent(eventData, clientData, uuid) {
        return {
            client: clientData,
            group: eventData.event.group,
            name: eventData.event.name,
            payload: eventData.event.parameters,
            ts: Date.now(),
            user: {
                geo: eventData.extraData.geo.toUpperCase(),
                id: eventData.extraData.userId,
                trackingUuid: uuid,
            },
            uuid: uuidv4(),
            version: eventData.event.version,
        };
    }
    buildUTMParameterFromQuery(query) {
        return {
            adId: String(query.ad_id ?? ''),
            adPosition: String(query.ad_position ?? ''),
            adsetId: String(query.adset_id ?? ''),
            adsetName: String(query.adset_name ?? ''),
            campaignId: String(query.campaign_id ?? ''),
            campaignType: String(query.campaign_type ?? ''),
            placement: String(query.placement ?? ''),
            query: String(query.query ?? ''),
            utmCampaign: String(query.utm_campaign ?? ''),
            utmContent: String(query.utm_content ?? ''),
            utmMedium: String(query.utm_medium ?? ''),
            utmSource: String(query.utm_source ?? ''),
            utmTerm: String(query.utm_term ?? ''),
        };
    }
    onConsentChanged() {
        if (!this.isCookieAllowed(REQUIRED_COOKIE)) {
            return;
        }
        const trackingData = this.getTidalTrackingData();
        if (this.overriddenTrackingUuid || !trackingData.uuid) {
            this.setTidalTrackingUUID(this.overriddenTrackingUuid || uuidv4());
        }
        this.setUtms();
    }
}
export class ClientTrackingApi extends BaseTrackingApi {
    constructor(clientOptions) {
        const { $cookieConsent, $cookies, moduleOptions, router } = clientOptions;
        super({
            $cookies: $cookies,
            moduleOptions,
            source: 'client',
        });
        this.clientOptions = clientOptions;
        // Events delayed until consent is given.
        this.pendingEvents = [];
        router.afterEach(() => this.onRouteChanged());
        this.query = new URLSearchParams(window.location.search);
        $cookieConsent.onConsentChanged(() => this.onConsentChanged());
    }
    triggerPageViewEvent(route, title = '') {
        this.triggerEvent({
            event: 'nuxtRoute',
            pageTitle: title,
            pageType: 'PageView',
            pageUrl: this.baseOptions.moduleOptions.baseUrl + route.fullPath,
            routeName: route.name ?? '',
        });
    }
    isCookieAllowed(cookieType) {
        return this.clientOptions.$cookieConsent.isCookieAllowed(cookieType);
    }
    async triggerEvent(eventData) {
        if (!this.isCookieAllowed(REQUIRED_COOKIE)) {
            this.logger.debug(`Queuing "${eventData.event}" event due to missing consent`);
            this.pendingEvents.push(() => this.triggerEvent(eventData));
            return;
        }
        const event = Object.assign({}, eventData);
        if (!event.eventId) {
            event.eventId = uuidv4();
        }
        // Trigger a client-side push to data layer for redundancy.
        if (typeof window !== 'undefined' && window.dataLayer) {
            window.dataLayer.push(event);
        }
        // Send the event to the server through proxy middleware.
        try {
            const { baseUrl } = this.baseOptions.moduleOptions;
            const { router } = this.clientOptions;
            const payload = {
                event,
                url: baseUrl + router.currentRoute.fullPath,
            };
            await this.clientOptions.axios.post('/t', payload);
        }
        catch (error) {
            this.logger.error('Failed dispatching a Tracking API request', error);
        }
    }
    async triggerCDFEvent(eventData) {
        if (this.isCookieAllowed(REQUIRED_COOKIE)) {
            await this.enrichAndSendCDFTidalEvent(eventData);
        }
        else {
            this.logger.debug(`Queuing CDF event due to missing consent ${eventData.event.name}`, JSON.stringify(eventData.event, null, 2));
            this.pendingEvents.push(() => Promise.resolve(this.enrichAndSendCDFTidalEvent(eventData)));
        }
    }
    async onConsentChanged() {
        super.onConsentChanged();
        if (!this.isCookieAllowed(REQUIRED_COOKIE)) {
            return;
        }
        if (this.pendingEvents.length) {
            const events = this.pendingEvents;
            this.pendingEvents = [];
            this.logger.debug(`Received consent. Sending ${events.length} pending events`);
            for (const event of events) {
                await event();
            }
        }
    }
    onRouteChanged() {
        this.pendingEvents = [];
    }
}
export class ServerTrackingApi extends BaseTrackingApi {
    constructor(serverOptions) {
        const { $cookieConsent, moduleOptions, req, res } = serverOptions;
        const cookies = cookieUniversal(req, res);
        super({
            $cookies: cookies,
            moduleOptions,
            source: 'server',
        });
        this.serverOptions = serverOptions;
        if (req.url) {
            try {
                this.query = new URL(req.url, moduleOptions.baseUrl).searchParams;
            }
            catch { /* empty */ }
        }
        if ($cookieConsent) {
            $cookieConsent.onConsentChanged(() => this.onConsentChanged());
        }
    }
    isCookieAllowed(cookieType) {
        return this.serverOptions.req.$isCookieAllowed(cookieType);
    }
    async triggerEvent(_event) {
        this.logger.error('triggerEvent() is not functional on the server-side.');
        return;
    }
    async triggerCDFEvent(eventData) {
        if (this.isCookieAllowed(REQUIRED_COOKIE)) {
            await this.enrichAndSendCDFTidalEvent(eventData);
        }
        else {
            this.logger.debug(`Dropping event ${eventData.event.name} due to no consent`);
        }
    }
}
/**
 * Dummy implementation that doesn't trigger any events.
 */
class DummyTrackingApi extends TrackingApi {
    async triggerEvent(_) { }
    getTidalTrackingData() {
        return {};
    }
    setTidalTrackingUUID(_) { }
    async triggerCDFEvent(_eventData) { }
    buildUTMParameterFromQuery(_query) {
        return {
            utmMedium: '',
            utmSource: '',
            utmCampaign: '',
            campaignType: '',
            campaignId: '',
            placement: '',
            utmContent: '',
            adId: '',
            adsetName: '',
            adsetId: '',
            utmTerm: '',
            adPosition: '',
            query: '',
        };
    }
}
export const dummyTrackingApi = new DummyTrackingApi();
